import React, { useEffect, useRef, useState } from 'react';
import { TradingViewChartProps } from './TradingViewChart.types';

// Add type definition for TradingView window property
declare global {
  interface Window {
    TradingView: any;
  }
}

export const TradingViewChart: React.FC<TradingViewChartProps> = ({ 
  symbol = 'SPY',
  height = '400px',
  width = '100%',
  dateRange
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<any>(null);
  const [widgetId, setWidgetId] = useState(`tradingview_chart_${Math.random().toString(36).substring(2, 9)}`);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const previousSymbol = useRef<string>(symbol);

  // Function to initialize the widget
  const initializeWidget = () => {
    if (!window.TradingView) {
      console.error("TradingView library not loaded");
      return;
    }
    
    if (containerRef.current) {
      containerRef.current.innerHTML = `<div id="${widgetId}" class="w-full h-full"></div>`;
    }
    
    try {
      widgetRef.current = new window.TradingView.widget({
        width: '100%',
        height: '100%',
        symbol: `${symbol}`,
        interval: 'D',
        timezone: 'Etc/UTC',
        theme: 'dark',
        style: '1',
        locale: 'en',
        enable_publishing: false,
        allow_symbol_change: true,
        container_id: widgetId,
        hide_top_toolbar: false,
        hide_legend: false,
        save_image: true,
        studies: [
          'MACD@tv-basicstudies',
          'RSI@tv-basicstudies',
          'Volume@tv-basicstudies'
        ],
        range: 'ytd', // Default to year-to-date if no date range provided
        overrides: {
          "mainSeriesProperties.candleStyle.drawWick": true,
          "mainSeriesProperties.candleStyle.drawBorder": true,
          "mainSeriesProperties.candleStyle.borderColor": "#378658",
          "mainSeriesProperties.candleStyle.borderUpColor": "#22ab94",
          "mainSeriesProperties.candleStyle.borderDownColor": "#e84752",
          "mainSeriesProperties.candleStyle.wickUpColor": "#22ab94",
          "mainSeriesProperties.candleStyle.wickDownColor": "#e84752",
          "mainSeriesProperties.candleStyle.upColor": "#22ab94",
          "mainSeriesProperties.candleStyle.downColor": "#e84752",
        },
        autosize: true,
      });
      
      // Apply date range if provided
      if (dateRange) {
        setCustomRange();
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error("Error initializing TradingView widget:", error);
      setIsLoading(false);
    }
  };

  // Load TradingView script only once
  useEffect(() => {
    // Load TradingView script if not already loaded
    if (!window.TradingView && !scriptRef.current) {
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => {
        if (window.TradingView) {
          initializeWidget();
        }
      };
      document.head.appendChild(script);
      scriptRef.current = script;
    } else if (window.TradingView) {
      // Script already loaded, initialize widget
      initializeWidget();
    }

    // Set up intersection observer for visibility
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []); // Empty dependency array - run only once

  // Handle symbol changes without recreating the widget
  useEffect(() => {
    if (symbol !== previousSymbol.current) {
      if (widgetRef.current && widgetRef.current.iframe) {
        try {
          // Try to change symbol via widget API first
          const chart = widgetRef.current.chart();
          if (chart && chart.setSymbol) {
            chart.setSymbol(symbol);
            previousSymbol.current = symbol;
            return;
          }
        } catch (e) {
          console.log("Could not access chart API, reinitializing widget");
        }
        
        // If changing symbol via API failed, reinitialize widget
        setWidgetId(`tradingview_chart_${Math.random().toString(36).substring(2, 9)}`);
        
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          initializeWidget();
        }, 100);
      } else if (window.TradingView) {
        // If widget not initialized yet, do it now
        initializeWidget();
      }
      
      previousSymbol.current = symbol;
    }
  }, [symbol]);

  // Function to set the custom range - now will be called when dateRange prop changes
  const setCustomRange = () => {
    if (!dateRange) return;
    
    const trySetRange = () => {
      if (widgetRef.current && widgetRef.current.iframe && widgetRef.current.iframe.contentWindow) {
        try {
          // Try to access the chart and set the range directly
          const chart = widgetRef.current.chart();
          if (chart && chart.setVisibleRange) {
            console.log("Setting date range:", {
              from: new Date(dateRange.from).toISOString(),
              to: new Date(dateRange.to).toISOString()
            });
            
            chart.setVisibleRange({ 
              from: dateRange.from.getTime() / 1000, 
              to: dateRange.to.getTime() / 1000 
            });
            return true;
          }
        } catch (e) {
          console.log("Chart not ready yet, retrying...", e);
          return false;
        }
      }
      return false;
    };
    
    // Try immediately and if fails, set interval to retry
    if (!trySetRange()) {
      const intervalId = setInterval(() => {
        if (trySetRange()) {
          clearInterval(intervalId);
        }
      }, 500);
      
      // Clear interval after 10 seconds to prevent infinite retries
      setTimeout(() => clearInterval(intervalId), 10000);
    }
  };
  
  // Effect to handle dateRange updates without recreating the widget
  useEffect(() => {
    if (widgetRef.current && dateRange) {
      setCustomRange();
    }
  }, [dateRange]);

  return (
    <div 
      ref={containerRef}
      className={`transform transition-all duration-1000 ease-out h-full ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
      }`}
      style={{ 
        height: height, 
        width: width 
      }}
    >
      <div 
        className={`relative w-full h-full transition-opacity duration-300 ${
          isLoading ? 'opacity-50' : 'opacity-100'
        }`}
      >
        <div
          id={widgetId}
          className="w-full h-full"
        />
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1EAEDB]"></div>
          </div>
        )}
      </div>
    </div>
  );
}; 